/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useState } from 'react';

interface PageHelpProps {
  title: string;
  steps: string[];
  tips?: string[];
  shortcuts?: Array<{ key: string; action: string }>;
}

export function PageHelp({ title, steps, tips, shortcuts }: PageHelpProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mb-4 border border-dds-blue-100 rounded-lg bg-dds-blue-50/40">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-2 text-sm text-dds-blue-600 hover:bg-dds-blue-50 rounded-lg transition-colors"
      >
        <span className="flex items-center gap-2">
          <span className="text-base">💡</span>
          <span className="font-medium">{title}</span>
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-4 pb-3 text-sm text-gray-700 space-y-3 border-t border-dds-blue-100">
          {/* Steps */}
          <div className="pt-3">
            <h4 className="font-medium text-gray-900 mb-1.5">How it works</h4>
            <ol className="list-decimal list-inside space-y-1 text-gray-600">
              {steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>

          {/* Tips */}
          {tips && tips.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-1.5">Tips</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                {tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Shortcuts */}
          {shortcuts && shortcuts.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-1.5">Keyboard shortcuts</h4>
              <div className="grid grid-cols-2 gap-1">
                {shortcuts.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono text-gray-700">{s.key}</kbd>
                    <span className="text-gray-600">{s.action}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
